import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clearTemporaryStatus } from '../utils/clearTemporaryStatus';
import '../styles/TemporaryAccountWarning.scss';

const TemporaryAccountWarning: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // First check localStorage for the most up-to-date user information
    let isTemporary = false;
    let tempEmail = '';
    let tempUserId = '';

    // Check localStorage first (most up-to-date source)
    const storedUserJson = localStorage.getItem('fitbuzz_user');
    if (storedUserJson) {
      try {
        const storedUser = JSON.parse(storedUserJson);
        console.log('Checking user from localStorage:', storedUser);

        // If we have a stored user, use its information
        isTemporary = !!storedUser.is_temporary;
        tempEmail = storedUser.email || '';
        tempUserId = storedUser.id || '';
      } catch (err) {
        console.error('Error parsing stored user:', err);
      }
    }

    // If not found in localStorage or not temporary, check session storage
    if (!isTemporary) {
      // Check if we have the temporary flag in session storage
      const sessionTemporary = sessionStorage.getItem('fitbuzz_temporary_account') === 'true';

      // Try to get user data from JSON in session storage
      const userDataJson = sessionStorage.getItem('fitbuzz_temp_user_data');
      let userData = null;

      if (userDataJson) {
        try {
          userData = JSON.parse(userDataJson);
          console.log('Retrieved user data from session storage:', userData);

          // Use the temporary flag from the user data if available
          isTemporary = sessionTemporary || !!userData.is_temporary;

          // Only set email and userId if not already set from localStorage
          if (!tempEmail) tempEmail = userData.email || '';
          if (!tempUserId) tempUserId = userData.id || '';
        } catch (err) {
          console.error('Error parsing user data from session storage:', err);
          isTemporary = sessionTemporary;
        }
      } else {
        isTemporary = sessionTemporary;
      }

      // Fall back to individual fields if needed
      if (!tempEmail) {
        tempEmail = sessionStorage.getItem('fitbuzz_temp_email') || '';
      }

      if (!tempUserId) {
        tempUserId = sessionStorage.getItem('fitbuzz_temp_user_id') || '';
      }
    }

    console.log('Final temporary status check:', { isTemporary, tempEmail, tempUserId });

    if (isAuthenticated && isTemporary) {
      setIsVisible(true);
      setEmail(tempEmail);
      setUserId(tempUserId);
    } else {
      setIsVisible(false);
    }
  }, [isAuthenticated]);

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleClearTemporaryStatus = () => {
    clearTemporaryStatus();
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="temporary-account-warning">
      <div className="warning-content">
        <div className="warning-icon">⚠️</div>
        <div className="warning-text">
          <p><strong>Your account is temporary.</strong> Please claim your account to ensure continued access.</p>
          <p className="small-text">Already claimed your account but still seeing this message? <button className="text-button" onClick={handleClearTemporaryStatus}>Click here</button> to clear this warning.</p>
        </div>
        <div className="warning-actions">
          <Link
            to={`/claim-account?email=${encodeURIComponent(email)}&user_id=${encodeURIComponent(userId)}`}
            className="claim-account-button"
          >
            Claim Now
          </Link>
          <button
            className="dismiss-button"
            onClick={handleDismiss}
            aria-label="Dismiss warning"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemporaryAccountWarning;
